import { Component } from '@verndale/core';
import { ISetTrackDataLayer, setTrackDataLayer } from '../helpers/dataLayer';

class Form extends Component {
  firstFilledInput: HTMLInputElement | null;
  formWasChanged: boolean;

  constructor(el: HTMLElement) {
    super(el);
    this.initForm();
    this.checkUrlParams();
    this.firstFilledInput = null;
    this.formWasChanged = false;
  }

  setupDefaults() {
    this.dom = {
      $formInputs: this.el.querySelectorAll<HTMLElement>('.FormTextbox__Input'),
      $formLabels: this.el.querySelectorAll<HTMLElement>('.Form__Element__Caption'),
      $formChoiceGroups: this.el.querySelectorAll<HTMLElement>('.FormChoice'),
      $showPasswordButtons: this.el.querySelectorAll('.show-password-btn'),
      $passwordInputs: this.el.querySelectorAll('.FormTextbox__Input--password[type="password"]'),
      $submitBtn: this.el.querySelector<HTMLElement>('.button'),
      $dataLayerSubmitBtn: this.el.querySelector<HTMLElement>('button[type="submit"]'),
      $countriesSelect: this.el.querySelector<HTMLElement>('.custom-form__countries-select'),
      $statesSelect: this.el.querySelector<HTMLElement>('.custom-form__states-select'),
      $postalCodeInput: this.el.querySelector<HTMLElement>('.custom-form__postal-input'),
      $nextBtn: this.el.querySelector<HTMLElement>('.subscription-form__step-button'),
      $description: this.el.querySelector<HTMLElement>('.custom-form__description')
    };
  }

  addListeners() {
    (this.dom.$formInputs as NodeList).forEach(formInput => {
      formInput.addEventListener('focus', this.handleInputFocus.bind(this));
      formInput.addEventListener('blur', this.handleInputBlur.bind(this));

      formInput.addEventListener('input', this.checkFirstInput.bind(this));
      formInput.addEventListener('blur', this.checkEachInput.bind(this));
    });

    if ((this.dom.$showPasswordButtons as NodeList).length > 0) {
      [...(this.dom.$showPasswordButtons as NodeList)].forEach(btn => {
        btn.addEventListener('click', this.togglePasswordVisibility.bind(this));
      });
    }

    if ((this.dom.$passwordInputs as NodeList).length > 1) {
      [...(this.dom.$passwordInputs as NodeList)].forEach(passwordInput => {
        passwordInput.addEventListener('input', this.handlePasswords.bind(this));
      });
    }

    if (this.dom.$countriesSelect) {
      (this.dom.$countriesSelect as HTMLSelectElement).addEventListener(
        'change',
        this.handleCountryChange.bind(this)
      );
    }

    if (this.dom.$dataLayerSubmitBtn) {
      (this.dom.$dataLayerSubmitBtn as HTMLButtonElement).addEventListener('click', this.submitDataLayerForm.bind(this));
    }

    if (this.dom.$nextBtn) {
      (this.dom.$nextBtn as HTMLButtonElement).addEventListener('click', this.handleNextStep.bind(this));
    }

    window.addEventListener('beforeunload', () => {
      if (this.formWasChanged) {
        setTrackDataLayer({
          event: 'formAbandon',
          digitalData: { form: { name: `${this.el.dataset.formName} - ${window.location.pathname}` } }
        });
        //   set a local storage item to check if the event was triggered
        // localStorage.setItem('formAbandon', 'true');
      }
    });
  }

  checkFirstInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value.trim() !== '' && !this.firstFilledInput) {
      this.firstFilledInput = input;
      if (this.firstFilledInput) {
        this.fillDataLayer('formStart', this.firstFilledInput);
      }
      (this.dom.$formInputs as NodeList).forEach(input => input.removeEventListener('input', this.checkFirstInput));
    }
  }

  checkEachInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value.trim() !== '') {
      this.fillDataLayer('formStepCompleted', input);
      input.removeEventListener('input', this.checkEachInput);
      this.formWasChanged = true;
    }
  }

  fillDataLayer(eventName: string, input: HTMLInputElement) {
    const formName = this.el.dataset.formName;
    const label = this.el.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
    const data = {
      event: eventName,
      digitalData: {
        form: {
          name: `${formName} - ${window.location.pathname}`,
          stepName: label ? label.innerText : ''
        }
      }
    };

    setTrackDataLayer(data);
  }

  submitDataLayerForm() {
    //  add delay to allow the form to validate

    setTimeout(() => {
      const errors = this.el.querySelectorAll('.ValidationFail');
      if (errors.length > 0) {
        this.checkErrorsForDataLayer([...errors]);
      } else {
        const formName = this.el.dataset.formName;
        const data = {
          event: 'formComplete',
          digitalData: {
            form: {
              name: `${formName} - ${window.location.pathname}`
            }
          }
        };
        setTrackDataLayer(data as ISetTrackDataLayer);
      }
    }, 500);
  }

  checkErrorsForDataLayer(errors: Element[]) {
    const errorFieldNames = errors.map((errorWrapper) => {
      const label = errorWrapper.querySelector('label');
      return label && label.innerText;
    });

    if (errorFieldNames.length > 0) {
      const formName = this.el.dataset.formName;
      const data = {
        event: 'formError',
        digitalData: {
          form: {
            name: `${formName} - ${window.location.pathname}`,
            errorFields: errorFieldNames
          }
        }
      };
      setTrackDataLayer(data as ISetTrackDataLayer);
    }
  }

  handleNextStep() {
    const description = this.dom.$description as HTMLElement;
    const nextBtn = this.dom.$nextBtn as HTMLElement;
    const form = this.el.querySelector('.Form__MainBody') as HTMLElement;

    if (form.classList.contains('hidden')) {
      form.classList.remove('hidden');
      description.style.display = 'none';
      nextBtn.style.display = 'none';
    }
  }

  cleaningPostalCodeAndState() {
    (this.dom.$statesSelect as HTMLSelectElement).value = '';
    (this.dom.$postalCodeInput as HTMLInputElement).value = '';
  }

  handleCountryChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    const value = target.value;
    const countriesList = target.dataset.countries;
    this.cleaningPostalCodeAndState();
    if (countriesList) {
      const countries = JSON.parse(countriesList);
      const parent = (this.dom.$postalCodeInput as HTMLSelectElement)?.parentElement as HTMLElement;

      if (parent) {
        if (!countries.includes(value)) {
          (parent as HTMLElement)?.classList.remove('Form__Element--wide');
        } else if (value !== 'US') {
          (parent as HTMLElement)?.classList.add('Form__Element--wide');
        } else {
          (parent as HTMLElement)?.classList.remove('Form__Element--wide');
        }
      }
    }
  }

  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const sku = urlParams.get('sku');
    const select = this.el.querySelector('.request-a-quote__select') as HTMLSelectElement;

    if (select && sku) {
      select.value = sku;
    }
  }

  handlePasswords(e: Event) {
    const target = e.target as HTMLInputElement;
    const parent = target.parentElement as HTMLElement;
    console.log(parent);
    const password = this.el.querySelector(
      '.FormTextbox__Input--password[data-new-password]'
    ) as HTMLInputElement;
    const passwordConfirmation = this.el.querySelector(
      '.FormTextbox__Input--password[data-password-confirmation]'
    ) as HTMLInputElement;
    const messageEl = this.el.querySelector('.custom-form__validation-password-criteria') as HTMLElement;

    parent.style.marginBlockEnd =  messageEl.offsetHeight + 'px';

    if (passwordConfirmation && password) {
      if (this.validatePassword(password.value)) {
        messageEl.style.display = 'none';
        if (passwordConfirmation.value.length > 3) {
          this.validatePasswordConfirmation(password.value, passwordConfirmation.value);
        }
      } else {
        if (password.value.length > 3) {
          messageEl.innerHTML =
            'Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character';
          messageEl.style.display = 'block';
        }
      }
    }
  }

  validatePasswordConfirmation(password: string, passwordConfirmation: string) {
    const messageEl = this.el.querySelector('.custom-form__validation-password-match') as HTMLElement;
    if (password !== passwordConfirmation) {
      messageEl.innerHTML = 'Passwords do not match';
      messageEl.style.display = 'block';
      if (this.dom.$submitBtn) {
        (this.dom.$submitBtn as HTMLButtonElement).disabled = true;
      }
    } else {
      messageEl.style.display = 'none';
      if (this.dom.$submitBtn) {
        (this.dom.$submitBtn as HTMLButtonElement).disabled = false;
      }
    }
  }

  validatePassword(password: string) {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  togglePasswordVisibility(e: Event) {
    const target = e.target as HTMLElement;
    const showPasswordBtn = target.closest('.show-password-btn') as HTMLElement;
    const passwordInput = showPasswordBtn.previousElementSibling as HTMLInputElement;

    if (passwordInput.type === 'password') {
      passwordInput.type = 'text';
      showPasswordBtn.classList.toggle('FormTextbox__ShowPassword--active', true);
    } else {
      passwordInput.type = 'password';
      showPasswordBtn.classList.toggle('FormTextbox__ShowPassword--active', false);
    }
  }

  initForm() {
    // initialize label and input status
    (this.dom.$formInputs as NodeList).forEach(formInput => {
      const inputEl = formInput as HTMLInputElement;
      const inputId = inputEl.id;
      const label = this.el.querySelector(`label[for="${inputId}"]`);

      if (inputEl.placeholder?.length && label) {
        label.innerHTML = inputEl.placeholder;
        inputEl.placeholder = '';
      }

      if (inputEl.value !== '') {
        label?.classList.toggle('Form__Element__Caption--floating', true);
      }
    });

    // clear labels for selects
    (this.dom.$formLabels as NodeList).forEach(formLabel => {
      const formLabelEl = formLabel as HTMLInputElement;
      const nextSibling = formLabelEl.nextElementSibling as HTMLElement;

      if (nextSibling.tagName.toUpperCase() === 'SELECT') {
        formLabelEl.style.display = 'none';
      }
    });

    // insert custom checkboxes
    (this.dom.$formChoiceGroups as NodeList).forEach(formChoiceGroup => {
      const formChoiceGroupEl = formChoiceGroup as HTMLElement;
      const formChoiceLabels = formChoiceGroupEl.querySelectorAll<HTMLElement>('label');

      formChoiceLabels.forEach(formChoiceLabel => {
        const formChoiceLabelEl = formChoiceLabel as HTMLElement;
        const checkbox = formChoiceLabelEl.querySelector(
          'input[type="checkbox"]'
        ) as HTMLInputElement;

        if (checkbox) {
          const customCheckbox = document.createElement('span');
          customCheckbox.classList.add('checkmark');
          formChoiceLabelEl.appendChild(customCheckbox);
        }
      });
    });
  }

  handleInputFocus(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const inputId = currentTarget.id;
    const label = this.el.querySelector(`label[for="${inputId}"]`);
    label?.classList.toggle('Form__Element__Caption--floating', true);
  }

  handleInputBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const inputId = currentTarget.id;
    const label = this.el.querySelector(`label[for="${inputId}"]`);

    if (currentTarget.value === '') {
      label?.classList.toggle('Form__Element__Caption--floating', false);
    }
  }
}

export default Form;
